import { useState } from 'react';
import Modal from 'react-modal';
import { Select } from 'antd';
import { BookOutlined, UserOutlined, FileTextOutlined, SafetyCertificateOutlined, SettingOutlined, TeamOutlined, BarChartOutlined, TrophyOutlined, ToolOutlined, QuestionCircleOutlined } from '@ant-design/icons';

const { Option } = Select;

const FAQ_DATA = [
    {
        category: 'General Questions',
        icon: <BookOutlined style={{ fontSize: '16px', color: '#4f46e5' }} />,
        items: [
            { q: 'What is this Learning Portal?', a: 'This portal is an online platform where you can access courses, track your progress, and improve your skills.' },
            { q: 'How do I enroll in a course?', a: 'Go to All Courses, select a course, and click on Enroll or Request Course.' },
            { q: 'Are all courses free?', a: 'Some courses are free, while others may require approval from your manager.' },
        ],
    },
    {
        category: 'My Learning',
        icon: <UserOutlined style={{ fontSize: '16px', color: '#059669' }} />,
        items: [
            { q: 'Where can I see my assigned courses?', a: 'You can check them under the Assigned section.' },
            { q: 'How do I track my progress?', a: 'Go to In Progress to see your current course status.' },
            { q: 'Where can I find completed courses?', a: 'All completed courses are available in the Completed section.' },
        ],
    },
    {
        category: 'Course Requests',
        icon: <FileTextOutlined style={{ fontSize: '16px', color: '#d97706' }} />,
        items: [
            { q: 'How can I request a course?', a: 'Go to All Courses, select a course, and click on Request Course.' },
            { q: 'Who approves my course request?', a: 'Your Reporting Manager (RM) will review and approve your request.' },
            { q: 'How do I check request status?', a: 'Go to Requested Courses to see the status.' },
        ],
    },
    {
        category: 'Certificates',
        icon: <SafetyCertificateOutlined style={{ fontSize: '16px', color: '#dc2626' }} />,
        items: [
            { q: 'When will I get my certificate?', a: 'You will receive a certificate after successfully completing a course.' },
            { q: 'Where can I download my certificate?', a: 'Go to My Certificates section to view and download.' },
        ],
    },
];

const ADMIN_FAQ_DATA = [
    {
        category: 'Course Management',
        icon: <SettingOutlined style={{ fontSize: '16px', color: '#4f46e5' }} />,
        items: [
            { q: 'How do I create a new course?', a: 'Go to Admin Center > Courses, click "New Course" button, fill in course details including title, description, category, modules, and media files, then save.' },
            { q: 'How do I add modules to a course?', a: 'While creating or editing a course, use the "Add Module" button to create modules. Each module can contain multiple media files (videos, PDFs, PPTs) and an optional test.' },
            { q: 'Can I edit a course after publishing?', a: 'Yes, go to Admin Center > Courses, find the course, click Edit. You can modify course details, add/remove modules, update media, and change settings.' },
            { q: 'How do I delete a course?', a: 'In Admin Center > Courses, find the course and click the Delete icon. Note: Deleting a course will remove all associated enrollments and progress data.' },
        ],
    },
    {
        category: 'User & Assignment Management',
        icon: <TeamOutlined style={{ fontSize: '16px', color: '#059669' }} />,
        items: [
            { q: 'How do I assign courses to users?', a: 'Go to Admin Center > Course Assignments, select users or groups, choose the course, set due dates if needed, and click Assign. Users will see the course in their Assigned section.' },
            { q: 'How do I approve course requests?', a: 'Go to Approvals section, review pending course requests, check user details and course information, then click Approve or Reject with optional comments.' },
            { q: 'Can I bulk assign courses?', a: 'Yes, in Course Assignments, you can select multiple users at once or use filters to select users by department, location, or role, then assign courses in bulk.' },
            { q: 'How do I deactivate a user assignment?', a: 'Go to Admin Center > Course Assignments, find the assignment, and click Deactivate. The user will lose access to the course but their progress data is preserved.' },
        ],
    },
    {
        category: 'Reports & Analytics',
        icon: <BarChartOutlined style={{ fontSize: '16px', color: '#d97706' }} />,
        items: [
            { q: 'Where can I see overall learning analytics?', a: 'The Dashboard shows key metrics including total enrollments, completion rates, average time, and trending courses. Use time range and category filters for detailed insights.' },
            { q: 'How do I track individual user progress?', a: 'Go to Reports & Analytics > User Progress, search for the user, and view their course enrollments, completion status, test scores, and time spent on each course.' },
            { q: 'Can I export reports?', a: 'Yes, most report pages have an Export button that allows you to download data in Excel or CSV format for further analysis.' },
            { q: 'How do I see course completion trends?', a: 'The Dashboard displays enrollment trends, completion rates over time, and category-wise analytics. Use the date range selector to view historical data.' },
        ],
    },
    {
        category: 'Tests & Certificates',
        icon: <TrophyOutlined style={{ fontSize: '16px', color: '#dc2626' }} />,
        items: [
            { q: 'How do I create a test for a module?', a: 'While editing a course module, click "Add Test", enter test name, passing percentage, and add questions with multiple choice options. Mark the correct answer for each question.' },
            { q: 'Can I set passing criteria for tests?', a: 'Yes, when creating a test, set the "Passing Percentage" field (e.g., 70%). Users must score at or above this percentage to pass the module.' },
            { q: 'How do I enable certificates for a course?', a: 'In course settings, toggle "Certificate Enabled" to Yes. Users who complete all modules and pass all tests will automatically receive a certificate.' },
            { q: 'Can I customize certificate templates?', a: 'Yes, certificate templates can be customized through the Admin Center. You can modify the design, add company logo, and change text fields.' },
        ],
    },
    {
        category: 'System Configuration',
        icon: <ToolOutlined style={{ fontSize: '16px', color: '#7c3aed' }} />,
        items: [
            { q: 'How do I manage categories and subcategories?', a: 'Go to Admin Center > Categories to create, edit, or delete course categories and subcategories. These help organize courses for easier discovery.' },
            { q: 'Can I set role-based permissions?', a: 'Yes, the system has predefined roles (Admin, Internal Admin, Internal Manager, Internal User, External User) with specific permissions. Contact system administrator to modify role access.' },
            { q: 'How do I configure notification settings?', a: 'Go to Admin Center > Notifications to configure email notifications for course assignments, approvals, completions, and reminders.' },
            { q: 'Where can I see system version and updates?', a: 'Click on Help & Support in the sidebar to see the current EDX version. System updates and release notes are communicated via announcements.' },
        ],
    },
];

export default function HelpFAQModal({ isOpen, onClose, isAdmin = false }) {
    const faqData = isAdmin ? ADMIN_FAQ_DATA : FAQ_DATA;
    const [selectedCategory, setSelectedCategory] = useState(faqData[0].category);
    const [openIndex, setOpenIndex] = useState(null);

    const currentFAQs = faqData.find(f => f.category === selectedCategory)?.items || [];

    const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="FAQs"
            style={{
                overlay: { backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' },
                content: {
                    position: 'relative', inset: 'unset',
                    width: '100%', maxWidth: '560px',
                    maxHeight: '85vh', borderRadius: '14px',
                    padding: '0', border: 'none',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                    overflow: 'hidden', fontFamily: 'Inter, sans-serif',
                },
            }}
        >
            {/* Header */}
            <div style={headerStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <QuestionCircleOutlined style={{ fontSize: '22px', color: '#fff' }} />
                    <div>
                        <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#fff' }}>Frequently Asked Questions</h2>
                        <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.75)' }}>Find answers to common questions</p>
                    </div>
                </div>
                <button onClick={onClose} style={closeBtnStyle}>✕</button>
            </div>

            <div style={{ padding: '20px', overflowY: 'auto', maxHeight: 'calc(85vh - 80px)', backgroundColor: '#f8fafc' }}>
                {/* Category Dropdown */}
                <div style={{ marginBottom: '18px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>
                        Category
                    </label>
                    <Select
                        value={selectedCategory}
                        onChange={(value) => { setSelectedCategory(value); setOpenIndex(null); }}
                        style={{ width: '100%' }}
                        size="large"
                        getPopupContainer={(trigger) => trigger.parentNode}
                        dropdownStyle={{ zIndex: 1200 }}
                    >
                        {faqData.map(f => (
                            <Option key={f.category} value={f.category}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {f.icon}
                                    <span>{f.category}</span>
                                </div>
                            </Option>
                        ))}
                    </Select>
                </div>

                {/* FAQ Accordion */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {currentFAQs.map((faq, i) => (
                        <div key={i} style={{ ...cardStyle, ...(openIndex === i ? cardActiveStyle : {}) }}>
                            <button onClick={() => toggle(i)} style={questionBtnStyle}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                    <span style={qBadgeStyle}>Q{i + 1}</span>
                                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#1a2332', textAlign: 'left', lineHeight: '1.4' }}>{faq.q}</span>
                                </div>
                                <span style={{ fontSize: '18px', color: '#667085', flexShrink: 0, transition: 'transform 0.2s', transform: openIndex === i ? 'rotate(180deg)' : 'rotate(0deg)' }}>⌄</span>
                            </button>
                            {openIndex === i && (
                                <div style={answerStyle}>
                                    <div style={{ width: '2px', backgroundColor: '#074870', borderRadius: '2px', flexShrink: 0, alignSelf: 'stretch' }} />
                                    <p style={{ margin: 0, fontSize: '13px', color: '#4a5568', lineHeight: '1.6' }}>{faq.a}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div style={{ marginTop: '20px', textAlign: 'center', padding: '12px', backgroundColor: '#eef2f7', borderRadius: '8px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#667085' }}>
                        Still have questions? Use <strong>Contact Admin</strong> once available.
                    </p>
                </div>
            </div>
        </Modal>
    );
}

const headerStyle = {
    background: 'linear-gradient(135deg, #074870 0%, #0c6fad 100%)',
    padding: '18px 20px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
};

const closeBtnStyle = {
    background: 'rgba(255,255,255,0.15)', border: 'none',
    color: '#fff', width: '30px', height: '30px',
    borderRadius: '50%', cursor: 'pointer', fontSize: '14px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const cardStyle = {
    backgroundColor: '#fff', borderRadius: '10px',
    border: '1px solid #e4e7ec',
    overflow: 'hidden', transition: 'box-shadow 0.2s',
};

const cardActiveStyle = {
    boxShadow: '0 2px 8px rgba(7,72,112,0.12)',
    border: '1px solid #b3d4ec',
};

const questionBtnStyle = {
    width: '100%', background: 'none', border: 'none',
    padding: '14px 16px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
};

const qBadgeStyle = {
    fontSize: '11px', fontWeight: '700', color: '#074870',
    backgroundColor: '#e0f0ff', borderRadius: '4px',
    padding: '2px 6px', flexShrink: 0, marginTop: '1px',
};

const answerStyle = {
    display: 'flex', gap: '12px',
    padding: '0 16px 14px 16px',
    borderTop: '1px solid #f0f4f8',
    paddingTop: '12px',
};
